// The toolbar Timers chip and its panel — one-click timers
// (docs/specs/timers-one-click-spec.md, Track L, with the 2026-10-14
// review-panel amendments and the firm's decisions).
//
// Rendered only by Layout, at the right end of the toolbar row, in the
// browser and in the desktop main window (also while the floating bar hides
// the ⏱ button).  Not a nav or quick-bar item.
//
// THE CHIP
//   • "⏱ Start timer" when I have no timers: one click starts one, headless
//     (no window opens, focus is not stolen).
//   • Otherwise ▶/⏸ for the current timer, its H:MM:SS and "+N".  It NEVER
//     shows a matter name (Rule 1.6 — the toolbar is on every screen share);
//     the panel does.
//   • No `title` attribute anywhere in this file: e2e's fab selectors are
//     `button[title*="Timer"]` / `button[title^="New Time Entry"]` /
//     `button[title*="Time Entry"]` with .first(), and the chip comes before
//     the ⏱ button in the DOM.  The chip is labelled aria-label="Your timers".
//   • It owns the 1 s tick (useTimers), only while something ticks or the
//     panel is open — Layout holds no elapsed state, so the app does not
//     re-render every second.
//
// THE PANEL — a popover (not a DraggablePopup, so it never dispatches
// minimize-time-popup): role="dialog", aria-label "Timers"; Esc or an outside
// click closes it; 360 px wide at zoom 1 (× the automatic zoom, --app-zoom:
// its text is rem, so a fixed 360 px wrapped every button at a 4K screen's
// 2.00), full width minus 16 px gutters on phones.
// One row per visible timer (lib/timerStore listMyTimers order: ticking
// first, then last activity), each with ▶/⏸, the live time (green ticking,
// amber paused), the matter or "No matter yet", the description's first 60
// characters, badges, "Fill in & save" and "Discard".
//
// ▶/⏸ (the chip and every row) do what the button showed — ▶ never pauses,
// ⏸ never resumes — once per double click (toggleTimerRow).  A ⏸ that paused
// the timer opens its form as "Fill in & save" does (James, 2026-10-15).  A
// timer an old-bundle window holds gets the command; its silence is reported.
//
// Every change goes through lib/timerStore.js (the slot is the clock).  The
// one-click helpers (hooks/useTimerEventBridge.js) are shared with Layout (the
// start-timer event, the tray, Ctrl/Cmd+Shift+T) and pop-out pages, so every
// one-click start has the same can_post_time gate, the same 1 s double-fire
// guard and the same messages.

import { useCallback, useEffect, useRef, useState } from 'react'
import timerStore, { MESSAGES } from '../lib/timerStore'
import useTimers from '../hooks/useTimers'
import {
  ONE_CLICK_GUARD_MS, createOneClickGuard, oneClickGuard, confirmVia, reportStoreResult,
  oneClickFields, startOneClickTimer, toggleOneClickTimer, openTimerFormFor,
  TOGGLE_GUARD_MS, createKeyGuard, toggleGuard, isRepeatClick, toggleTimerRow
} from '../hooks/useTimerEventBridge'
import { hasTimerBar } from '../lib/desktopCapabilities'
import { BAR_PROTO_VERSION, NO_MATTER_LABEL, formatBarElapsed, readBarProto } from '../lib/timerSlots'
import { hasPermission } from '../lib/navItems'
import { useToast } from '../context/ToastContext'
import { useAsk } from '../context/PromptProvider'
import api from '../services/api'

// The one-click helpers live with the window-event bridge
// (hooks/useTimerEventBridge.js): Layout, this panel and pop-out pages share
// one can_post_time gate, one 1 s double-fire guard and one set of messages.
// Re-exported here for the panel's callers and tests.
export {
  ONE_CLICK_GUARD_MS, createOneClickGuard, oneClickGuard, confirmVia, reportStoreResult,
  oneClickFields, startOneClickTimer, toggleOneClickTimer, openTimerFormFor,
  TOGGLE_GUARD_MS, createKeyGuard, toggleGuard, isRepeatClick, toggleTimerRow
}

const isDesktop = (electronAPI) => !!(electronAPI && typeof electronAPI.openPopupWindow === 'function')
const windowElectron = () => {
  try { return typeof window !== 'undefined' ? window.electronAPI : undefined } catch { return undefined }
}

// Attorney names for the "On behalf of X" badge, fetched once per page and
// only when a row needs one.  A failed fetch falls back to a generic label.
let teamNamesCache = null
export function resetTeamNamesCache () { teamNamesCache = null }
function useTeamNames (needed) {
  const [names, setNames] = useState(teamNamesCache)
  useEffect(() => {
    if (!needed) return undefined
    if (teamNamesCache) { setNames(teamNamesCache); return undefined }
    let live = true
    api.get('/api/team').then((r) => {
      const m = {}
      for (const u of (Array.isArray(r && r.data) ? r.data : [])) {
        if (u && u.id != null) m[String(u.id)] = u.name || u.email || ''
      }
      teamNamesCache = m
      if (live) setNames(m)
    }).catch(() => {
      teamNamesCache = {}
      if (live) setNames({})
    })
    return () => { live = false }
  }, [needed])
  return names || {}
}

const DESCRIPTION_CHARS = 60
const shortDescription = (d) => {
  const s = String(d || '').trim()
  return s.length > DESCRIPTION_CHARS ? `${s.slice(0, DESCRIPTION_CHARS)}…` : s
}

function Badge ({ tone = 'gray', children }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-700 border-gray-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200'
  }
  return (
    <span className={`inline-block text-[11px] leading-4 px-1.5 py-0.5 rounded border ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  )
}

function TimerRow ({ row, desktop, names, busy, onToggle, onFillIn, onDiscard }) {
  const matter = row.hasMatter ? row.matterTitle : ''
  const name = matter || NO_MATTER_LABEL
  const disabled = !!row.claimed || busy
  const ticking = !!row.ticking
  const description = shortDescription(row.description)
  const behalf = row.onBehalfOf ? (names[String(row.onBehalfOf)] || 'another attorney') : null
  return (
    <li data-testid="timer-row" data-key={row.key} className="py-2.5 border-b border-gray-100 last:border-b-0">
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={(ev) => onToggle(row, ev)}
          disabled={disabled}
          aria-label={ticking ? `Pause the timer: ${name}` : `Resume the timer: ${name}`}
          className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm text-white disabled:opacity-50 ${ticking ? 'bg-amber-500 hover:bg-amber-600' : 'bg-green-600 hover:bg-green-700'}`}
        >
          <span aria-hidden="true">{ticking ? '⏸' : '▶'}</span>
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span data-testid="timer-row-elapsed"
              className={`font-mono text-sm font-semibold ${ticking ? 'text-green-700' : 'text-amber-700'}`}>
              {formatBarElapsed(row.elapsed)}
            </span>
            {matter
              ? <span className="text-sm text-gray-800 truncate">{matter}</span>
              : <span className="text-sm text-amber-700 truncate">{NO_MATTER_LABEL}</span>}
          </div>
          <div className={`text-xs truncate ${description ? 'text-gray-600' : 'text-gray-400 italic'}`}>
            {description || MESSAGES.noDescriptionLabel}
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            {row.isResume && row.resumeEntryId && <Badge tone="purple">Updating entry #{row.resumeEntryId}</Badge>}
            {behalf && <Badge tone="blue">On behalf of {behalf}</Badge>}
            {row.longRunning && <Badge tone="red">{MESSAGES.longRunning(row.elapsed)}</Badge>}
            {row.futureStart && <Badge tone="red">{MESSAGES.clockChanged}</Badge>}
            {row.cls === 'v2' && row.heldElsewhere && (
              <Badge>{desktop ? 'Open in another window' : 'Open in another tab'}</Badge>
            )}
            {row.fromBeforeUpdate && <Badge tone="amber">From before the update</Badge>}
            {row.postUncertain && <Badge tone="amber">Save not confirmed — open it and click Save again</Badge>}
            {row.claimed && <Badge>Saving…</Badge>}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-1.5">
            <button type="button" onClick={() => onFillIn(row)} disabled={disabled}
              className="text-xs font-medium text-primary-700 hover:underline disabled:opacity-50 disabled:no-underline">
              Fill in &amp; save
            </button>
            {row.cls === 'v1-held'
              ? <span className="text-xs text-gray-500">{MESSAGES.discardInItsWindow}</span>
              : (
                <button type="button" onClick={() => onDiscard(row)} disabled={disabled}
                  className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50 disabled:no-underline">
                  Discard
                </button>
              )}
          </div>
        </div>
      </div>
    </li>
  )
}

export default function TimersPanel ({
  user,
  store = timerStore,
  onOpenInPage,
  onNewEntry,
  barVisible = false,
  electronAPI
}) {
  const canPost = hasPermission(user, 'can_post_time')
  const toast = useToast()
  const ask = useAsk()
  const [open, setOpen] = useState(false)
  const [error, setError] = useState('')
  const [storageBroken, setStorageBroken] = useState(false)
  const [busy, setBusy] = useState(false)
  const { timers, current, count } = useTimers({ store, tick: canPost, forceTick: canPost && open, reconcile: false })
  const rootRef = useRef(null)
  const chipRef = useRef(null)
  const firstActionRef = useRef(null)
  // While a confirm dialog is up, a click in it is not an "outside click".
  const confirmingRef = useRef(false)
  // The open panel shows refusals inline; with it closed (the chip's ▶/⏸)
  // they come as a toast.
  const openRef = useRef(open)
  openRef.current = open
  const e = electronAPI !== undefined ? electronAPI : windowElectron()
  const desktop = isDesktop(e)
  const names = useTeamNames(open && timers.some(r => r.onBehalfOf))

  const askTracked = useCallback(async (opts) => {
    confirmingRef.current = true
    try { return await ask(opts) } finally { confirmingRef.current = false }
  }, [ask])
  const onStorage = useCallback(() => setStorageBroken(true), [])

  // Esc and outside clicks close the panel (listeners attached after this
  // click finishes, so the opening click does not close it again).
  useEffect(() => {
    if (!open) return undefined
    const onKey = (ev) => {
      if (ev.key !== 'Escape' || confirmingRef.current) return
      // Claimed: this Esc closes the panel only, not also Calendar's full
      // screen (a window listener, which runs after this document one).
      ev.preventDefault()
      setOpen(false)
      try { chipRef.current && chipRef.current.focus() } catch { /* gone */ }
    }
    const onDown = (ev) => {
      if (confirmingRef.current) return
      if (rootRef.current && rootRef.current.contains(ev.target)) return
      setOpen(false)
    }
    const t = setTimeout(() => {
      document.addEventListener('mousedown', onDown)
      document.addEventListener('touchstart', onDown)
    }, 0)
    document.addEventListener('keydown', onKey)
    try { firstActionRef.current && firstActionRef.current.focus() } catch { /* not yet rendered */ }
    return () => {
      clearTimeout(t)
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
    }
  }, [open])

  const run = useCallback(async (fn) => {
    setError('')
    setBusy(true)
    try {
      const r = await fn()
      if (openRef.current && r && !r.ok && r.reason !== 'cancelled' && r.reason !== 'guarded' && r.reason !== 'storage' && r.message) setError(r.message)
      return r
    } finally {
      setBusy(false)
    }
  }, [])

  const start = useCallback(() => run(() => startOneClickTimer({
    store, user, toast, ask: askTracked, onStorage, warn: !openRef.current
  })), [run, store, user, toast, askTracked, onStorage])

  // ▶/⏸ (the chip and every row): what the button showed is what is done,
  // once per double click (toggleTimerRow — the second click of a double
  // click used to undo the first).  A dropped click does not even clear the
  // panel's error line.  A ⏸ that paused the timer then opens its form the
  // way Fill in & save does, and closes the panel (James, 2026-10-15); ▶
  // opens nothing.  The pause stands if the form cannot open — that message
  // shows inline (panel open) or as a toast (the chip).
  const toggle = useCallback((row, ev) => {
    if (isRepeatClick(ev)) return undefined
    return run(async () => {
      const r = await toggleTimerRow(row, {
        store, ask: askTracked, toast, onStorage, warn: !openRef.current,
        openForm: { onOpenInPage, electronAPI: e }
      })
      const form = r && r.form
      if (form && form.ok) setOpen(false)
      return form && !form.ok ? form : r
    })
  }, [run, store, toast, askTracked, onStorage, onOpenInPage, e])

  // A row whose timer went since the panel drew it: why, when the store
  // knows (matter.blocked removed a resume timer), else "saved or discarded".
  const goneMessage = useCallback((row) => {
    let note = null
    try {
      note = typeof store.removalNote === 'function'
        ? store.removalNote(row.key, { tid: row.slot ? row.slot.tid : undefined })
        : null
    } catch { note = null }
    return (note && note.message) || MESSAGES.missing
  }, [store])

  const fillIn = useCallback((row) => run(async () => {
    const r = await openTimerFormFor(row, {
      store, ask: askTracked, toast, onOpenInPage, electronAPI: e, onStorage, warn: !openRef.current
    })
    if (r && r.ok) setOpen(false)
    if (r && !r.ok && r.reason === 'missing') return { ...r, message: goneMessage(row) }
    return r
  }), [run, store, askTracked, toast, onOpenInPage, e, onStorage, goneMessage])

  const discard = useCallback((row) => run(async () => {
    const fresh = store.getTimer(row.key)
    if (!fresh) return { ok: false, reason: 'missing', message: goneMessage(row) }
    if (fresh.cls === 'v1-held') return { ok: false, reason: 'held', message: MESSAGES.discardInItsWindow }
    // Written by old code that another tab or window may still be running
    // (v1-unknown): ONE confirm that says so as well (not the adopt confirm
    // and then the discard confirm).  Yes discards it here — force, since the
    // adopt question was in that confirm; Cancel writes nothing.
    const old = fresh.cls === 'v1-unknown'
    const oldElsewhere = old && await store.adoptNeedsConfirm(row.key)
    const what = {
      elapsed: formatBarElapsed(fresh.elapsed),
      matter: fresh.hasMatter ? fresh.matterTitle : '',
      isResume: !!fresh.isResume
    }
    const yes = await askTracked({
      type: 'confirm',
      danger: true,
      confirmLabel: 'Discard',
      message: oldElsewhere ? MESSAGES.discardOldConfirm(what) : MESSAGES.discardConfirm(what)
    })
    if (yes !== true) return { ok: false, reason: 'cancelled' }
    let r = store.discard(row.key, { force: old })
    // Rewritten by old code after it was read here: that adopt confirm is
    // the first question about it.
    if (!r.ok && r.reason === 'needs-adopt') {
      const a = await store.adoptV1(row.key, { confirm: confirmVia(askTracked) })
      r = a.ok ? store.discard(row.key) : a
    }
    reportStoreResult(r, { toast, onStorage, warn: false })
    return r
  }), [run, store, askTracked, toast, onStorage, goneMessage])

  const newEntry = useCallback(() => {
    setOpen(false)
    if (typeof onNewEntry === 'function') onNewEntry()
  }, [onNewEntry])

  if (!canPost) return null

  const barNeedsRestart = (() => {
    if (!barVisible) return false
    try {
      return hasTimerBar() && readBarProto(window.localStorage) < BAR_PROTO_VERSION
    } catch { return false }
  })()

  const ticking = !!(current && current.ticking)
  const more = count > 1 ? count - 1 : 0

  return (
    <div ref={rootRef} className="relative no-print" data-testid="timers-chip">
      {count === 0 ? (
        <button
          type="button"
          ref={chipRef}
          onClick={start}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          <span aria-hidden="true">⏱</span> Start timer
        </button>
      ) : (
        <div className="inline-flex items-stretch rounded-lg border border-gray-300 bg-white overflow-hidden">
          <button
            type="button"
            onClick={(ev) => toggle(current, ev)}
            disabled={busy || !!current.claimed}
            aria-label={ticking ? 'Pause the current timer' : 'Resume the current timer'}
            className={`px-2 text-sm hover:bg-gray-50 disabled:opacity-60 ${ticking ? 'text-amber-600' : 'text-green-700'}`}
          >
            <span aria-hidden="true">{ticking ? '⏸' : '▶'}</span>
          </button>
          <button
            type="button"
            ref={chipRef}
            onClick={() => setOpen(o => !o)}
            aria-label="Your timers"
            aria-haspopup="dialog"
            aria-expanded={open}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm border-l border-gray-200 hover:bg-gray-50"
          >
            <span data-testid="timers-chip-elapsed"
              className={`font-mono font-semibold ${ticking ? 'text-green-700' : 'text-amber-700'}`}>
              {formatBarElapsed(current.elapsed)}
            </span>
            {more > 0 && <span className="text-xs font-semibold text-gray-600">+{more}</span>}
          </button>
        </div>
      )}

      {open && (
        <div
          role="dialog"
          aria-label="Timers"
          className="fixed left-2 right-2 top-16 z-[55] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[calc(360px*var(--app-zoom,1))] bg-white rounded-xl shadow-2xl border border-gray-200 text-left"
        >
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
            <button type="button" ref={firstActionRef} onClick={start} disabled={busy}
              className="text-sm font-medium px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-60">
              + Start timer
            </button>
            <button type="button" onClick={newEntry}
              className="text-sm font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
              New entry (type hours)
            </button>
          </div>
          {(storageBroken || error) && (
            <div role="alert" className="mx-3 mt-2 rounded-lg bg-red-50 text-red-700 text-xs px-2.5 py-2">
              {storageBroken ? MESSAGES.storage : error}
            </div>
          )}
          {barNeedsRestart && (
            <p className="mx-3 mt-2 rounded-lg bg-amber-50 text-amber-800 text-xs px-2.5 py-2">{MESSAGES.restartBar}</p>
          )}
          <div className="px-3 max-h-[60vh] overflow-y-auto">
            {timers.length === 0
              ? <p className="py-4 text-sm text-gray-500">{MESSAGES.empty}</p>
              : (
                <ul aria-label="Open timers">
                  {timers.map(row => (
                    <TimerRow key={row.key} row={row} desktop={desktop} names={names} busy={busy}
                      onToggle={toggle} onFillIn={fillIn} onDiscard={discard} />
                  ))}
                </ul>
              )}
          </div>
          <p className="px-3 py-2 border-t border-gray-100 text-[11px] text-gray-500">{MESSAGES.footer}</p>
        </div>
      )}
    </div>
  )
}
